/**
 * <p-video-slider> — Video-Karussell der Perso-PDPs (sections/p-video-slider.liquid).
 * Baut auf <p-loop-slider> (assets/p-loop-slider.js: Loop, Drag, Pfeile, Zentrierung) auf und ergänzt:
 *  - Hyper-Coverflow: Karten skalieren nach Abstand zur Mitte (1 − 0,1 × Abstand / Kartenbreite)
 *  - aktives (mittiges) Video spielt stumm im Loop, alle anderen pausieren
 *  - Play/Pause-Knopf je Karte ([data-video-toggle])
 */
customElements.whenDefined('p-loop-slider').then(() => {
  if (customElements.get('p-video-slider')) return;
  const Base = customElements.get('p-loop-slider');
  const SPEED = 500;

  class PVideoSlider extends Base {
    connectedCallback() {
      super.connectedCallback();
      if (this.videoReady || !this.track) return;
      this.videoReady = true;

      this.addEventListener('click', (event) => {
        const toggle = event.target.closest('[data-video-toggle]');
        if (!toggle || !this.contains(toggle)) return;
        const video = toggle.closest('.p-vs__slide')?.querySelector('video');
        if (!video) return;
        if (video.paused) {
          this.pauseAll(video);
          this.play(video);
        } else {
          video.pause();
          this.syncButton(video);
        }
      });

      // Erst abspielen, wenn der Slider sichtbar ist
      this.visible = false;
      new IntersectionObserver((entries) => {
        this.visible = entries.some((entry) => entry.isIntersecting);
        if (this.visible) this.playActive();
        else this.pauseAll();
      }).observe(this);
    }

    move(animate, extra = 0) {
      super.move(animate, extra);
      this.scaleSlides(animate, extra);
      if (extra) return; // während des Ziehens nichts umschalten
      // Nach der Slide-Animation (500 ms) das mittige Video starten — per Timer statt transitionend,
      // damit es auch ohne laufende Transition (z. B. reduzierte Bewegung) zuverlässig greift.
      clearTimeout(this.playTimer);
      this.playTimer = setTimeout(() => this.playActive(), animate ? SPEED + 50 : 0);
    }

    /* Skalierung aus der Zielposition des Tracks berechnen (wie Swiper-Coverflow, scale 0.9) */
    scaleSlides(animate, extra) {
      if (!this.track || !this.viewport) return;
      const translate = (this.enabled ? this.position(this.index) : 0) + extra;
      const center = this.viewport.clientWidth / 2;
      [...this.track.children].forEach((slide) => {
        const width = slide.offsetWidth || 1;
        const slideCenter = slide.offsetLeft + translate + width / 2;
        const distance = Math.abs(center - slideCenter) / width;
        slide.style.transition = animate ? `transform ${SPEED}ms ease` : 'none';
        slide.style.transform = `scale(${Math.max(0, 1 - 0.1 * distance)})`;
        slide.toggleAttribute('data-active', distance < 0.5);
      });
    }

    activeVideo() {
      const active = this.track?.querySelector('[data-active]');
      return active?.querySelector('video') ?? null;
    }

    playActive() {
      const video = this.activeVideo();
      this.pauseAll(video);
      if (video && this.visible) this.play(video);
    }

    play(video) {
      // play() lädt ein preload="none"-Video selbst nach (preload hier nicht ändern: bricht play() ab)
      video.play()?.catch(() => {});
      this.syncButton(video);
      video.addEventListener('playing', () => this.syncButton(video), { once: true });
    }

    pauseAll(except = null) {
      this.track?.querySelectorAll('video').forEach((video) => {
        if (video === except) return;
        if (!video.paused) video.pause();
        this.syncButton(video);
      });
    }

    syncButton(video) {
      const button = video.closest('.p-vs__slide')?.querySelector('[data-video-toggle]');
      if (!button) return;
      const playing = !video.paused;
      button.classList.toggle('is-playing', playing);
      button.setAttribute('aria-label', playing ? button.dataset.labelPause : button.dataset.labelPlay);
    }
  }

  customElements.define('p-video-slider', PVideoSlider);
});
